
import { Reducer } from 'interfaces'
import { AdvisorDataState } from 'components/features/coaching/advisorStore/interfaces/state'
import {
  CREATE_ADVISOR_MEETING,
  CreateAdvisorMeeting,
  AdvisorsRequestAction,
  GetAdvisorTimeslotsAction,
} from 'components/features/coaching/advisorStore/actions/advisorMeetingActions'
import { TimeSlot } from 'components/features/coaching/advisorMeeting/types/requests'
import { ADVISOR_TIMESLOT_BUSY } from 'constants/errors'
import { normalize, concatCollections, setItemInCollection } from 'helpers/normalizeHelpers'
import { createEmptyFetchable } from 'helpers/loadableHelpers'

const isTimeslotBusyError = (action: CreateAdvisorMeeting): action is Extract<CreateAdvisorMeeting, { meta: 'fail' }> => {
  if (action.meta !== 'fail') {
    return false
  }

  const { errorsMap } = action.payload
  const non_field_errors = errorsMap && errorsMap['non_field_errors']
  if (!non_field_errors) {
    return false
  }

  if ((non_field_errors as any) !== ADVISOR_TIMESLOT_BUSY && non_field_errors[0] !== ADVISOR_TIMESLOT_BUSY) {
    return false
  }

  return true
}

export const timeslotsReducer: Reducer<AdvisorDataState['timeslots']> = (prevState = {}, action) => {
  switch (action.type) {
    case AdvisorsRequestAction.type: {
      if (action.meta !== 'success') {
        return prevState
      }

      const { timeslots } = action.payload.response

      return Object.keys(timeslots).reduce((accState, advisorUid) => {
        const advisorTimeslotsArr = timeslots[advisorUid].map((advisorTimeslot) => new TimeSlot(advisorTimeslot))

        let advisorTimeslots = normalize(advisorTimeslotsArr, 'date')
        if (prevState[advisorUid]) {
          advisorTimeslots = concatCollections(prevState[advisorUid], advisorTimeslots)
        }

        return {
          ...accState,
          [advisorUid]: advisorTimeslots,
        }
      }, prevState)
    }

    case GetAdvisorTimeslotsAction.type: {
      if (action.meta !== 'success') {
        return prevState
      }

      const { timeslots } = action.payload.response

      return Object.keys(timeslots).reduce((accState, advisorUid) => {
        const advisorTimeslotsArr = timeslots[advisorUid].map((advisorTimeslot) => new TimeSlot(advisorTimeslot))

        return {
          ...accState,
          [advisorUid]: normalize(advisorTimeslotsArr, 'date'),
        }
      }, prevState)
    }

    case CREATE_ADVISOR_MEETING: {
      if (!isTimeslotBusyError(action)) {
        return prevState
      }

      const { timeslot: date, advisor_uid } = action.payload.params
      const advisorTimeslots = prevState[advisor_uid]
      if (!advisorTimeslots) {
        return prevState
      }

      const advisorTimeslot = advisorTimeslots.byKey[date]
      if (!advisorTimeslot) {
        return prevState
      }

      return {
        ...prevState,
        [advisor_uid]: setItemInCollection(advisorTimeslots, {
          ...advisorTimeslot,
          isAvailable: false,
        }, 'date'),
      }
    }
  }
  return prevState
}


export const timeslotsPaginationReducer: Reducer<AdvisorDataState['timeslotsPagination']> = (prevState = {}, action) => {
  switch (action.type) {
    case GetAdvisorTimeslotsAction.type:
    case AdvisorsRequestAction.type: {
      if (action.meta !== 'success') {
        return prevState
      }

      const { timeslotsPagination } = action.payload.response
      return {
        ...prevState,
        ...timeslotsPagination,
      }
    }
  }
  return prevState
}

export const timeslotsRequestReducer: Reducer<AdvisorDataState['timeslotsRequest']> = (prevState = {}, action) => {
  switch (action.type) {
    case GetAdvisorTimeslotsAction.type: {
      const { advisor_uid } = action.payload.params
      const prevRequest = prevState[advisor_uid] || createEmptyFetchable()

      if (action.meta === 'request') {
        return {
          ...prevState,
          [advisor_uid]: {
            ...prevRequest,
            isLoading: true,
          },
        }
      }

      return {
        ...prevState,
        [advisor_uid]: {
          ...prevRequest,
          isLoading: false,
        },
      }
    }
  }
  return prevState
}
